import React from "react";
import { FaTimes } from "react-icons/fa";
import { FiAlertTriangle } from "react-icons/fi";

const ViewRiskModal = ({ isOpen, onClose, risk }) => {
  if (!isOpen || !risk) return null;

  const getLevelColor = (value) => {
    switch (value) {
      case "High":
        return "text-red-600";
      case "Medium":
        return "text-yellow-600";
      case "Low":
      default:
        return "text-green-600";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Open":
        return "bg-red-100 text-red-700";
      case "In Progress":
        return "bg-yellow-100 text-yellow-700";
      case "Closed":
      case "Resolved":
        return "bg-green-100 text-green-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl relative transform transition-all duration-300 ease-out max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                <FiAlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-black">
                  Risk Details:{" "}
                  <span className="text-gray-900">{risk.title}</span>
                </h2>
                <div className="flex items-center mt-1 text-black text-sm">
                  <FiAlertTriangle className="w-3 h-3 mr-1" />
                  <span>Risk Management</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
            >
              <FaTimes size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-y-auto p-4 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-sm text-gray-700">
            <div className="space-y-3">
              <p>
                <strong>Title:</strong> {risk.title}
              </p>
              <p>
                <strong>Description:</strong> {risk.description}
              </p>
              <p>
                <strong>Category:</strong> {risk.category}
              </p>
              <p>
                <strong>Causes:</strong> {risk.causes}
              </p>
              <p>
                <strong>Consequences:</strong> {risk.consequences}
              </p>
              <p>
                <strong>Mitigation:</strong> {risk.mitigation}
              </p>
              <p>
                <strong>Contingency Plan:</strong> {risk.contingencyPlan}
              </p>
            </div>

            <div className="space-y-3">
              <p>
                <strong>Likelihood:</strong>{" "}
                <span className={getLevelColor(risk.likelihood)}>
                  {risk.likelihood}
                </span>
              </p>
              <p>
                <strong>Impact:</strong>{" "}
                <span className={getLevelColor(risk.impact)}>
                  {risk.impact}
                </span>
              </p>
              <p>
                <strong>Rating:</strong>{" "}
                <span className={getLevelColor(risk.rating)}>
                  {risk.rating}
                </span>
              </p>
              <p>
                <strong>Risk Owner:</strong>{" "}
                {risk.riskOwner
                  ? `${risk.riskOwner.firstName} ${risk.riskOwner.lastName}`
                  : "N/A"}
              </p>
              <p>
                <strong>Status:</strong>{" "}
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                    risk.status
                  )}`}
                >
                  {risk.status}
                </span>
              </p>
            </div>
          </div>

          {/* Treatments / Actions */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Treatment / Actions
            </h3>
            {risk.treatments && risk.treatments.length > 0 ? (
              <ul className="list-disc list-inside ml-3 text-gray-600 space-y-1">
                {risk.treatments.map((treatment, index) => (
                  <li key={index}>
                    <span className="font-medium">{treatment.action}</span> –{" "}
                    <span>
                      {treatment.responsible ||
                        treatment.riskOwner?.name ||
                        "—"}
                    </span>{" "}
                    (
                    <span className="italic">
                      {treatment.dueDate || "No due date"}
                    </span>
                    , <span>{treatment.status || "Pending"}</span>)
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 italic">No actions added</p>
            )}
          </div>

          {/* Comments */}
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Comments
            </h3>
            <p className="text-gray-600">{risk.comments || "No comments"}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewRiskModal;



// import React from "react";
// import { FaTimes } from "react-icons/fa";

// const ViewRiskModal = ({ isOpen, onClose, risk }) => {
//   if (!isOpen || !risk) return null;

//   const getLevelColor = (value) => {
//     if (value >= 4) return "text-red-600";
//     if (value >= 2) return "text-yellow-600";
//     return "text-green-600";
//   };

//   const getStatusColor = (status) => {
//     switch (status) {
//       case "Open":
//         return "bg-red-100 text-red-700";
//       case "In Progress":
//         return "bg-yellow-100 text-yellow-700";
//       case "Closed":
//         return "bg-green-100 text-green-700";
//       default:
//         return "bg-gray-100 text-gray-700";
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
//       <div className="bg-white rounded shadow-lg w-11/12 max-w-2xl p-6 relative">
//         <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800">
//           <FaTimes size={20} />
//         </button>
//         <h2 className="text-xl font-semibold text-orange-600 mb-4">View Risk Details</h2>
//         <div className="space-y-2 text-sm text-gray-700">
//           <p><strong>Title:</strong> {risk.title}</p>
//           <p><strong>Description:</strong> {risk.description}</p>
//           <p><strong>Category:</strong> {risk.category}</p>
//           <p><strong>Causes:</strong> {risk.causes}</p>
//           <p><strong>Consequences:</strong> {risk.consequences}</p>
//           <p><strong>Likelihood:</strong> <span className={getLevelColor(risk.likelihood)}>{risk.likelihood}</span></p>
//           <p><strong>Impact:</strong> <span className={getLevelColor(risk.impact)}>{risk.impact}</span></p>
//           <p><strong>Rating:</strong> <span className={getLevelColor(risk.rating)}>{risk.rating}</span></p>
//           <p><strong>Mitigation:</strong> {risk.mitigation}</p>
//           <p><strong>Contingency Plan:</strong> {risk.contingencyPlan}</p>
//           <p><strong>Risk Owner:</strong> {risk.riskOwner}</p>
//           <p><strong>Comments:</strong> {risk.comments}</p>

//           {/* ✅ Treatments / Actions */}
//           <div>
//             <strong>Treatment / Actions:</strong>
//             {risk.treatments && risk.treatments.length > 0 ? (
//               <ul className="list-disc list-inside ml-3 text-gray-600">
//                 {risk.treatments.map((treatment, index) => (
//                   <li key={index}>
//                     <span className="font-medium">{treatment.action}</span> –{" "}
//                     <span>{treatment.responsible || treatment.riskOwner || "—"}</span> (
//                     <span className="italic">{treatment.dueDate || "No due date"}</span>,{" "}
//                     <span>{treatment.status || "Pending"}</span>)
//                   </li>
//                 ))}
//               </ul>
//             ) : (
//               <p className="text-gray-500 italic">No actions added</p>
//             )}
//           </div>

//           <p>
//             <strong>Status:</strong>{" "}
//             <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(risk.status)}`}>
//               {risk.status}
//             </span>
//           </p>
//         </div>
//         <div className="mt-6 text-right">
//           <button onClick={onClose} className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700">
//             Close
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ViewRiskModal;